import React, { useEffect, useState } from 'react';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Modal from '@salesforce/design-system-react/components/modal';
import Button from '@salesforce/design-system-react/components/button';
import Checkbox from '@salesforce/design-system-react/components/checkbox';
import { IBaseTask } from 'interfaces/IUpgradeSchema.interface';

export interface CQManualTaskModalProps {
    activeTask: IBaseTask;
    onProceed: (isManualTaskCompleted?:boolean) => Promise<void>;
}

/**
 * Renders a modal dialog for completing manual steps associated with a CQ task.
 *
 * The `CQManualTaskModal` component displays a list of manual sub-tasks (steps) for the currently active task.
 * Each step can be marked as complete using a toggle checkbox. The "Done" button is enabled only when all steps
 * are marked as complete. Upon completion, the state is persisted in localStorage for each step and the active task.
 */
export function CQManualTaskModal(props: CQManualTaskModalProps) {
    const manualSteps = props?.activeTask?.inputs?.subTasks as IBaseTask[] || [];
    const [isModalOpen, setIsModalOpen] = useState(true);
    const [completionState, setCompletionState] = useState<boolean[]>([]);

    useEffect(() => {
        setIsModalOpen(true);
        setCompletionState(manualSteps.map(() => false));
    }, [props.activeTask]);

    /**
     * Handles the change event for a checkbox in a list, updating the completion state.
     */
    function handleCheckBOxChange(index: number, name: string, checked: boolean) {
        const newState = [...completionState];
        newState[index] = checked;
        setCompletionState(newState);
    }

    /**
     * Mark active task as completed in localStorage,
     * using its unique name as key. Closes the modal upon completion.
     */
    function handleDone() {
        setIsModalOpen(false);
        props.onProceed(true);
    }

    /**
     * Renders a step's name in bold and its description as HTML content.
     */
    function renderStepNameAndDescription(name: string, description?: string) {
        return (
            <div className="slds-col slds-size_10-of-12">
                <strong>{name}</strong>
                <div dangerouslySetInnerHTML={{ __html: description || '' }} />
            </div>
        );
    }

    /**
     * Renders a checkbox component for marking a task as complete.
     */
    function renderCheckbox(index: number,name: string) {
        return (
            <div className="slds-col slds-size_2-of-12">
                <Checkbox
                    labels={{toggleDisabled:'Mark Complete', toggleEnabled:'Completed'}}
                    checked={completionState[index]}
                    onChange={e => handleCheckBOxChange(index, name, e.target.checked)}
                    variant="toggle"
                />
            </div>
        );
    }

    /**
     * Renders the details of a step, including its name, optional description, and a checkbox.
     */
    function renderStepDetails(task:IBaseTask, index: number) {
        const name = task.inputs?.taskName;
        const description = task.inputs?.description;
        return (
            <div key={name} className="slds-grid slds-gutters slds-m-vertical_small">
                {renderStepNameAndDescription(name, description)}
                {renderCheckbox(index,name)}
            </div>
        );
    }

    /**
     * Renders the content of the manual task modal.
     *
     * If there are no manual steps, displays a message indicating that no steps are available.
     * Otherwise, maps each manual step to its corresponding rendered details using `renderStepDetails`.
     */
    function renderModalContent() {
        if (manualSteps.length === 0) return <div>No steps available.</div>;
        return manualSteps.map(renderStepDetails);
    }

    /**
     * Renders the footer "Done" button for the modal.
     */
    function renderFooterButton(disabled: boolean) {
        return (
            <Button
                key="done-button"
                label="Done"
                variant="brand"
                onClick={handleDone}
                disabled={disabled}
            />
        );
    }

    /**
     * Renders the CQ Deployment Agent modal dialog.
     *
     * The modal displays content generated by `renderModalContent()` and includes a footer button
     * rendered by `renderFooterButton`, which is disabled until all tasks in `completionState` are completed.
     */
    function renderModal() {
        const allCompleted = completionState.every(Boolean);
        return (
            <Modal
                footer={[renderFooterButton(!allCompleted)]}
                isOpen={isModalOpen}
                disableClose
                size="medium"
                title="CQ Deployment Agent"
            >
                <div className="slds-m-around_large">
                    {renderModalContent()}
                </div>
            </Modal>
        );
    }

    return (
        <IconSettings iconPath="/assets/icons">
            {renderModal()}
        </IconSettings>
    );
}
